import type { ApiClient } from '@carbonwebmail/api-client'
import { BOUNCE_RATE_LIMIT } from '@carbonwebmail/contracts'
import { useEffect, useState } from 'react'
import { percent } from '../ui/format.js'

/**
 * The two facts the frame itself shows: how many sends are unknown, and whether
 * the bounce rate is near the level that gets a sending domain suspended.
 *
 * They live in the shell rather than on the dashboard because they are the two
 * things that should follow you around. A number that only appears on a screen
 * nobody has open is a number nobody sees.
 *
 * Everything is undefined until the answer arrives, and stays undefined if it
 * never does. Undefined is not zero: zero says "nothing needs you", and saying
 * that before you know is how a badge stops being believed.
 */

export interface ShellStatus {
  readonly unknownSends: number | undefined
  readonly bounceWarning: { readonly rate: string; readonly limit: string } | undefined
}

const NOTHING: ShellStatus = { unknownSends: undefined, bounceWarning: undefined }

/** Warn from four fifths of the way to the limit, not on arrival at it. */
const WARN_AT = 0.8

export function useShellStatus(client: ApiClient, signedIn: boolean): ShellStatus {
  const [status, setStatus] = useState<ShellStatus>(NOTHING)

  useEffect(() => {
    if (!signedIn) {
      setStatus(NOTHING)
      return
    }

    let current = true
    void client
      .dashboard()
      .then((summary) => {
        if (!current) return
        setStatus({
          unknownSends: summary.unknown,
          bounceWarning:
            summary.sentThisWeek > 0 && summary.bounceRate >= BOUNCE_RATE_LIMIT * WARN_AT
              ? { rate: percent(summary.bounceRate), limit: percent(BOUNCE_RATE_LIMIT) }
              : undefined,
        })
      })
      .catch(() => {
        // The frame is not the place to report a failed request. The dashboard
        // says so plainly on its own screen; a banner in the chrome of every
        // page would be noise someone learns to look past.
        if (current) setStatus(NOTHING)
      })

    return () => {
      current = false
    }
  }, [client, signedIn])

  return status
}
